import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import logoUrl from '../../assets/images/Senerfi-formato.png';

function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return raw;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function formatTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.slice(0, 5);
}

function formatMoney(value) {
  const num = Number(value || 0) || 0;
  try {
    return num.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch (_) {
    const fixed = num.toFixed(2);
    const [whole, decimal] = fixed.split('.');
    return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal || '00'}`;
  }
}

function sanitizeFileName(value) {
  return String(value || 'orden_servicio')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_');
}

async function resolveLogoSource(customLogoUrl) {
  const basePath = (process.env.PUBLIC_URL || '/').replace(/\/+$/g, '');
  const candidates = [
    String(customLogoUrl || '').trim(),
    `${basePath || ''}${logoUrl}`,
    `${basePath || ''}/Senerfi-formato.png`,
  ].filter(Boolean);

  const toAbsolute = (source) => {
    const value = String(source || '').trim();
    if (!value) return '';
    if (/^data:image\//i.test(value)) return value;
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith('/')) return value;
    return `${basePath || ''}/${value}`.replace(/\/{2,}/g, '/');
  };

  for (const candidate of candidates) {
    const absolute = toAbsolute(candidate);
    if (!absolute) continue;
    if (/^data:image\//i.test(absolute)) return absolute;
    try {
      const response = await fetch(absolute, { cache: 'no-store' });
      if (!response.ok) continue;
      const blob = await response.blob();
      if (blob?.type && !String(blob.type).toLowerCase().startsWith('image/')) continue;
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(blob);
      });
      if (dataUrl) return dataUrl;
    } catch (_) {}
  }

  return '';
}

async function openServiceOrderFrame({ order = {}, company = {}, logoUrl: customLogoUrl }) {
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.name = 'PRINT_SERVICE_ORDER';
  document.body.appendChild(frame);

  const win = frame.contentWindow;
  const doc = win.document;
  doc.open();

  const logoSrc = await resolveLogoSource(customLogoUrl);
  const companyName = company?.name || 'SENERFI S.A.S.';
  const companySubtitle = company?.subtitle || 'Soluciones energeticas forestales integrales';
  const title = sanitizeFileName(order?.order_number || `orden_servicio_${order?.plate || 'vehiculo'}`);

  const maintenanceType = String(order?.maintenance_type || '').trim();
  const maintenanceChecks = ['Correctivo', 'Preventivo', 'Otro']
    .map((label) => `
      <div class="check-item ${maintenanceType === label ? 'active' : ''}">
        <span class="box">${maintenanceType === label ? 'X' : ''}</span>
        <span>${esc(label)}</span>
      </div>
    `)
    .join('');

  const headerLogo = logoSrc
    ? `<img src="${esc(logoSrc)}" style="max-height:58px; object-fit:contain;" />`
    : `<div class="fallback-logo">${esc(companyName)}</div>`;

  doc.write(`<!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>
      * { box-sizing: border-box; }
      body { margin: 0; background: #ffffff; font-family: Arial, Helvetica, sans-serif; color: #111827; }
      .sheet { width: 900px; margin: 0 auto; padding: 12px; }
      .header { border: 1px solid #111827; display: grid; grid-template-columns: 180px 1fr 190px; }
      .header > div { min-height: 88px; padding: 8px 10px; }
      .header-center { border-left: 1px solid #111827; border-right: 1px solid #111827; display: flex; flex-direction: column; justify-content: center; text-align: center; }
      .header-title { font-size: 13px; font-weight: 800; text-transform: uppercase; line-height: 1.25; }
      .header-subtitle { margin-top: 4px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
      .header-meta { font-size: 11px; line-height: 1.55; }
      .header-meta b { font-weight: 800; }
      .fallback-logo { font-size: 18px; font-weight: 800; padding-top: 16px; }
      .grid-4, .grid-3, .grid-2 { display: grid; gap: 0; margin-top: 8px; }
      .grid-4 { grid-template-columns: repeat(4, 1fr); }
      .grid-3 { grid-template-columns: repeat(3, 1fr); }
      .grid-2 { grid-template-columns: repeat(2, 1fr); }
      .field { border: 1px solid #111827; min-height: 66px; margin-left: -1px; margin-top: -1px; }
      .field-label { background: #f3f4f6; border-bottom: 1px solid #111827; padding: 5px 7px; font-size: 10px; font-weight: 800; text-transform: uppercase; min-height: 28px; }
      .field-value { padding: 8px 7px; font-size: 12px; min-height: 36px; white-space: pre-wrap; word-break: break-word; }
      .section-title { margin-top: 10px; border: 1px solid #111827; background: #fde7d7; padding: 6px 8px; font-size: 11px; font-weight: 800; text-transform: uppercase; }
      .textarea { min-height: 150px; }
      .textarea-sm { min-height: 95px; }
      .checks { display: flex; flex-wrap: wrap; gap: 12px; padding: 8px 7px; min-height: 36px; align-items: center; }
      .check-item { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; }
      .check-item .box { width: 16px; height: 16px; border: 1px solid #111827; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800; }
      .sign-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; margin-top: 8px; }
      .signature { border: 1px solid #111827; min-height: 120px; margin-left: -1px; }
      .signature .sig-body { min-height: 78px; padding: 10px 7px 0; font-size: 12px; }
      .signature .sig-line { margin: 0 7px 8px; border-top: 1px solid #9ca3af; height: 16px; }
      .footer-note { margin-top: 8px; font-size: 10px; color: #4b5563; text-align: right; }
    </style>
  </head>
  <body>
    <div class="sheet">
      <div class="header">
        <div>${headerLogo}</div>
        <div class="header-center">
          <div class="header-title">${esc(companySubtitle)}</div>
          <div class="header-subtitle">Ingreso y salida de taller</div>
        </div>
        <div class="header-meta">
          <div><b>CODIGO:</b> FT-TRP-130</div>
          <div><b>VERSION:</b> 0</div>
          <div><b>FORMATO:</b> Orden de servicio</div>
        </div>
      </div>

      <div class="grid-4">
        <div class="field">
          <div class="field-label">Numero orden de servicio</div>
          <div class="field-value">${esc(order?.order_number || '')}</div>
        </div>
        <div class="field">
          <div class="field-label">Fecha orden de servicio</div>
          <div class="field-value">${esc(formatDate(order?.order_date))}</div>
        </div>
        <div class="field">
          <div class="field-label">Fecha de entrada</div>
          <div class="field-value">${esc(formatDate(order?.entry_date))}</div>
        </div>
        <div class="field">
          <div class="field-label">Hora de entrada</div>
          <div class="field-value">${esc(formatTime(order?.entry_time))}</div>
        </div>
      </div>

      <div class="grid-4">
        <div class="field">
          <div class="field-label">Nombre del taller al cual ingresa vehiculo</div>
          <div class="field-value">${esc(order?.workshop_name || '')}</div>
        </div>
        <div class="field">
          <div class="field-label">Responsable del mantenimiento</div>
          <div class="field-value">${esc(order?.maintenance_responsible_name || '')}</div>
        </div>
        <div class="field">
          <div class="field-label">Nombre de quien reporta el hallazgo</div>
          <div class="field-value">${esc(order?.reporter_name || '')}</div>
        </div>
        <div class="field">
          <div class="field-label">Tipo de mantenimiento</div>
          <div class="checks">${maintenanceChecks}</div>
        </div>
      </div>

      <div class="section-title">Datos del vehiculo</div>
      <div class="grid-4">
        <div class="field">
          <div class="field-label">Placa</div>
          <div class="field-value">${esc(order?.plate || '')}</div>
        </div>
        <div class="field">
          <div class="field-label">Modelo</div>
          <div class="field-value">${esc(order?.model || '')}</div>
        </div>
        <div class="field">
          <div class="field-label">Kilometraje</div>
          <div class="field-value">${esc(order?.mileage || '')}</div>
        </div>
        <div class="field">
          <div class="field-label">Tipo de vehiculo</div>
          <div class="field-value">${esc(order?.vehicle_type || '')}</div>
        </div>
      </div>

      <div class="section-title">Descripcion de la causa principal</div>
      <div class="field">
        <div class="field-value textarea">${esc(order?.main_cause || '')}</div>
      </div>

      <div class="section-title">Descripcion de intervencion</div>
      <div class="field">
        <div class="field-value textarea">${esc(order?.intervention_description || '')}</div>
      </div>

      <div class="section-title">Observaciones</div>
      <div class="field">
        <div class="field-value textarea-sm">${esc(order?.observations || '')}</div>
      </div>

      <div class="grid-4">
        <div class="field">
          <div class="field-label">Fecha de salida</div>
          <div class="field-value">${esc(formatDate(order?.exit_date))}</div>
        </div>
        <div class="field">
          <div class="field-label">Hora de salida</div>
          <div class="field-value">${esc(formatTime(order?.exit_time))}</div>
        </div>
        <div class="field">
          <div class="field-label">Costo total</div>
          <div class="field-value">$ ${esc(formatMoney(order?.total_cost || 0))}</div>
        </div>
        <div class="field">
          <div class="field-label">Ordenador del gasto</div>
          <div class="field-value">${esc(order?.spend_authorizer_name || '')}</div>
        </div>
      </div>

      <div class="section-title">Responsables y firmas</div>
      <div class="sign-grid">
        <div class="signature">
          <div class="field-label">Conductor responsable del ingreso</div>
          <div class="sig-body">${esc(order?.driver_delivery_name || '')}</div>
          <div class="sig-line"></div>
        </div>
        <div class="signature">
          <div class="field-label">Coordinador / supervisor mantenimiento y transporte</div>
          <div class="sig-body">${esc(order?.coordinator_name || '')}</div>
          <div class="sig-line"></div>
        </div>
        <div class="signature">
          <div class="field-label">Quien recibe el vehiculo en taller</div>
          <div class="sig-body">${esc(order?.receiver_name || '')}</div>
          <div class="sig-line"></div>
        </div>
        <div class="signature">
          <div class="field-label">Responsable del mantenimiento en taller</div>
          <div class="sig-body">${esc(order?.workshop_responsible_name || '')}</div>
          <div class="sig-line"></div>
        </div>
      </div>

      <div class="footer-note">FT-TRP-130 | Orden de servicio | ${esc(formatDate(order?.order_date))}</div>
    </div>
    <script>
      window.onload = () => { window.__SERVICE_ORDER_READY__ = true; };
    </script>
  </body>
  </html>`);
  doc.close();

  return { frame, win, title };
}

export async function generateServiceOrderPdfBlob({ order, company, logoUrl: customLogoUrl }) {
  const { frame, win, title } = await openServiceOrderFrame({ order, company, logoUrl: customLogoUrl });
  try {
    for (let i = 0; i < 60; i += 1) {
      if (win.__SERVICE_ORDER_READY__) break;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    const images = Array.from(win.document.images || []);
    await Promise.all(images.map((img) => (
      img.complete ? Promise.resolve() : new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      })
    )));
    await new Promise((resolve) => setTimeout(resolve, 60));

    const target = win.document.querySelector('.sheet') || win.document.body;
    const renderScale = 2;
    const canvas = await html2canvas(target, {
      scale: renderScale,
      backgroundColor: '#FFFFFF',
      useCORS: true,
      windowWidth: 980,
      windowHeight: 1800,
    });

    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'letter' });
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 5;
    const pxToMm = 0.26458333333333334;
    const mmW = (canvas.width / renderScale) * pxToMm;
    const mmH = (canvas.height / renderScale) * pxToMm;
    const maxW = pageW - (margin * 2);
    const pageInnerH = pageH - (margin * 2);
    const fitScale = Math.min(1, maxW / mmW);
    const drawW = mmW * fitScale;
    const drawH = mmH * fitScale;

    let offsetY = 0;
    pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    while (offsetY + pageInnerH < drawH) {
      offsetY += pageInnerH;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    }

    return { blob: pdf.output('blob'), title };
  } finally {
    try { frame.remove(); } catch (_) {}
  }
}

